import { Box, useTheme, alpha } from "@mui/material";
import ReactQuill from "react-quill";
import { Editor_ModuleConfig } from "../../constant/ApplicationVariables";
import "react-quill/dist/quill.snow.css";

// Helper function to get Quill styles based on theme
const getQuillStyles = (theme) => {
  const isDark = theme.palette.mode === 'dark';
  
  return {
    toolbar: {
      backgroundColor: isDark ? theme.palette.background.paper : theme.palette.background.default,
      borderColor: theme.palette.divider,
      borderWidth: "1px",
      borderStyle: "solid",
      borderRadius: `${theme.shape.borderRadius} ${theme.shape.borderRadius} 0 0`,
      "& .ql-formats": {
        marginRight: theme.spacing(1),
      },
      "& button": {
        color: theme.palette.text.secondary,
        "&:hover": {
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.action.hover,
          "& .ql-stroke": {
            stroke: theme.palette.primary.main,
          },
          "& .ql-fill": {
            fill: theme.palette.primary.main,
          },
        },
        "&.ql-active": {
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.action.selected,
          "& .ql-stroke": {
            stroke: theme.palette.primary.main,
          },
          "& .ql-fill": {
            fill: theme.palette.primary.main,
          },
        },
      },
      "& .ql-picker": {
        color: theme.palette.text.secondary,
        fontSize: "0.875rem",
        "&:hover": {
          color: theme.palette.primary.main,
        },
        "&.ql-expanded": {
          color: theme.palette.primary.main,
          "& .ql-picker-label": {
            "& .ql-stroke": {
              stroke: theme.palette.primary.main,
            },
          },
        },
        "& .ql-picker-label": {
          padding: "2px 8px",
          fontSize: "0.875rem",
          "&:hover": {
            color: theme.palette.primary.main,
          },
          "&::before": {
            fontSize: "0.875rem",
          },
        },
        "& .ql-picker-options": {
          backgroundColor: isDark ? theme.palette.grey[800] : theme.palette.grey[100],
          borderColor: theme.palette.divider,
          borderRadius: theme.shape.borderRadius,
          padding: theme.spacing(1),
          fontSize: "0.875rem",
          "& .ql-picker-item": {
            padding: theme.spacing(0.5, 1),
            fontSize: "0.875rem",
            "&:hover": {
              color: theme.palette.primary.main,
              backgroundColor: theme.palette.action.hover,
            },
            "&.ql-selected": {
              color: theme.palette.primary.main,
              backgroundColor: theme.palette.action.selected,
              fontWeight: 500,
            },
          },
        },
      },
    },
    container: {
      backgroundColor: isDark ? theme.palette.background.default : theme.palette.background.paper,
      borderColor: theme.palette.divider,
      borderWidth: "1px",
      borderStyle: "solid",
      borderTop: "none",
      borderRadius: `0 0 ${theme.shape.borderRadius} ${theme.shape.borderRadius}`,
      "& .ql-editor": {
        color: theme.palette.text.primary,
        minHeight: "100px",
        fontSize: "0.875rem",
        lineHeight: 1.5,
        "& a": {
          color: theme.palette.primary.main,
          textDecoration: "underline",
          "&:hover": {
            color: theme.palette.primary.dark,
          },
        },
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          color: theme.palette.text.primary,
        },
        "& blockquote": {
          borderLeft: `3px solid ${theme.palette.primary.main}`,
          color: theme.palette.text.secondary,
          paddingLeft: theme.spacing(2),
          marginLeft: 0,
        },
        "& pre": {
          backgroundColor: isDark ? theme.palette.grey[900] : theme.palette.grey[100],
          borderRadius: theme.shape.borderRadius,
          padding: theme.spacing(1),
          fontSize: "0.875rem",
        },
        "& code": {
          backgroundColor: isDark ? theme.palette.grey[900] : theme.palette.grey[100],
          padding: theme.spacing(0.25, 0.5),
          borderRadius: theme.shape.borderRadius / 2,
          fontSize: "0.875rem",
        },
      },
      // FIX: Correct placeholder selector for dark mode
      "& .ql-editor.ql-blank::before": {
        color: isDark ? alpha(theme.palette.common.white, 0.5) : alpha(theme.palette.common.black, 0.5),
        fontStyle: "italic",
        fontSize: "0.875rem",
        content: "attr(data-placeholder)",
        pointerEvents: "none",
      },
    },
    tooltip: {
      backgroundColor: isDark ? theme.palette.grey[800] : theme.palette.grey[100],
      borderColor: theme.palette.divider,
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[3],
      color: theme.palette.text.primary,
      fontSize: "0.875rem",
      "& input[type=text]": {
        borderColor: theme.palette.divider,
        borderRadius: theme.shape.borderRadius / 2,
        backgroundColor: isDark ? theme.palette.grey[900] : theme.palette.background.paper,
        fontSize: "0.875rem",
        "&:focus": {
          borderColor: theme.palette.primary.main,
          outline: "none",
        },
      },
      "& a.ql-action::after": {
        color: theme.palette.primary.main,
        "&:hover": {
          color: theme.palette.primary.dark,
        },
      },
      "& a.ql-remove::before": {
        color: theme.palette.error.main,
      },
    },
  };
};

export default function CustomEditor({ 
  value, 
  handleUpdateValue, 
  placeholderMessage, 
  containerFixedHeight, 
  editorHeight 
}) {
  const theme = useTheme();
  const quillStyles = getQuillStyles(theme);

  const handleChange = (val) => {
    handleUpdateValue(val);
  };

  return (
    <Box 
      className="w-full flex flex-col gap-0"
      sx={{
        height: containerFixedHeight ? containerFixedHeight : "131px",
        overflowY: "auto",
        fontFamily: theme.typography.fontFamily,
        // Toolbar styles
        "& .ql-toolbar": quillStyles.toolbar,
        // Container styles
        "& .ql-container": {
          ...quillStyles.container,
          maxHeight: editorHeight ? editorHeight : "83%",
          minHeight: editorHeight ? editorHeight : "83%",
          overflow: "hidden",
          "& .ql-editor": quillStyles.container["& .ql-editor"],
        },
        // Tooltip styles
        "& .ql-snow .ql-tooltip": quillStyles.tooltip,
        // Global active/hover states
        "& .ql-snow.ql-toolbar button:hover, .ql-snow .ql-toolbar button:hover, .ql-snow.ql-toolbar button:focus, .ql-snow .ql-toolbar button:focus, .ql-snow.ql-toolbar button.ql-active, .ql-snow .ql-toolbar button.ql-active": {
          color: theme.palette.primary.main,
        },
        "& .ql-snow.ql-toolbar .ql-picker-label:hover, .ql-snow .ql-toolbar .ql-picker-label:hover, .ql-snow.ql-toolbar .ql-picker-label.ql-active, .ql-snow .ql-toolbar .ql-picker-label.ql-active": {
          color: theme.palette.primary.main,
        },
        "& .ql-snow.ql-toolbar .ql-picker-item:hover, .ql-snow .ql-toolbar .ql-picker-item:hover, .ql-snow.ql-toolbar .ql-picker-item.ql-selected, .ql-snow .ql-toolbar .ql-picker-item.ql-selected": {
          color: theme.palette.primary.main,
        },
        // SVG icon colors for active states
        "& .ql-snow .ql-stroke": {
          stroke: theme.palette.text.secondary,
        },
        "& .ql-snow .ql-fill": {
          fill: theme.palette.text.secondary,
        },
        "& .ql-snow button:hover .ql-stroke, .ql-snow button:focus .ql-stroke, .ql-snow button.ql-active .ql-stroke, .ql-snow .ql-picker-label:hover .ql-stroke, .ql-snow .ql-picker-label.ql-active .ql-stroke": {
          stroke: theme.palette.primary.main,
        },
        "& .ql-snow button:hover .ql-fill, .ql-snow button:focus .ql-fill, .ql-snow button.ql-active .ql-fill, .ql-snow .ql-picker-label:hover .ql-fill, .ql-snow .ql-picker-label.ql-active .ql-fill": {
          fill: theme.palette.primary.main,
        },
        // Ensure dropdown text is properly sized
        "& .ql-snow .ql-picker.ql-header .ql-picker-label::before, .ql-snow .ql-picker.ql-font .ql-picker-label::before, .ql-snow .ql-picker.ql-size .ql-picker-label::before, .ql-snow .ql-picker.ql-header .ql-picker-item::before, .ql-snow .ql-picker.ql-font .ql-picker-item::before, .ql-snow .ql-picker.ql-size .ql-picker-item::before": {
          fontSize: "0.875rem",
        },
      }}
    >
      <ReactQuill
        theme="snow"
        value={value}
        onChange={handleChange}
        placeholder={placeholderMessage ? placeholderMessage : "Write something..."}
        modules={{ ...Editor_ModuleConfig }}
      />
    </Box>
  );
}